// Material UI Payments Component
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import {
    Box,
    Card,
    CardContent,
    Typography,
    Paper,
    Tabs,
    Tab,
    Container,
    makeStyles,
    Button,
} from '@material-ui/core'
import { AttachMoney, CreditCard, Person } from '@material-ui/icons'

import { TenantBillTab } from './TenantBillTab'
import { DelinquencyTab } from './DelinquencyTab'
import { TenantBillSettings } from './TenantBillSettings'
import { useRent } from '../../hooks/useRent'
import { useTimezone } from '../../contexts/timezone'

const useStyles = makeStyles((theme) => ({
    container: {
        margin: 0,
        padding: 0,
    },
    header: {
        marginBottom: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    summaryCards: {
        display: 'flex',
        gap: theme.spacing(3),
        marginBottom: theme.spacing(1),
        flexWrap: 'wrap',
    },
    summaryCard: {
        flex: 1,
        minWidth: 250,
    },
    cardContent: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    iconBox: {
        width: 48,
        height: 48,
        borderRadius: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    successIcon: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.main,
    },
    warningIcon: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.main,
    },
    primaryIcon: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
    },
    errorIcon: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.main,
    },

    settingsContainer: {
        padding: theme.spacing(3),
        maxWidth: 800,
    },
}))

export const Payments = () => {
    const classes = useStyles()

    const { currentTimezone } = useTimezone()

    const [currentTab, setCurrentTab] = useState(0)

    const { useRentAnalytics, createStripeLoginLink } = useRent()
    const { data: analytics, isLoading: analyticsLoading } = useRentAnalytics()

    const handleTabChange = (
        event: React.ChangeEvent<{}>,
        newValue: number,
    ) => {
        setCurrentTab(newValue)
    }

    return (
        <Container maxWidth="xl" className={classes.container}>
            {/* Header */}
            <Box className={classes.header}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Payment Management
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        Track payments, fees, and delinquencies
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                        try {
                            const url = await createStripeLoginLink()
                            window.open(url, '_blank')
                        } catch (error: any) {
                            toast.error(
                                error.response?.data?.error ||
                                    'Failed to open Stripe dashboard',
                            )
                        }
                    }}
                >
                    Stripe Dashboard
                </Button>
            </Box>

            {/* Summary Cards */}
            <Box className={classes.summaryCards}>
                <Box className={classes.summaryCard}>
                    <Card>
                        <CardContent>
                            <Box className={classes.cardContent}>
                                <Box
                                    className={`${classes.iconBox} ${classes.successIcon}`}
                                >
                                    <AttachMoney />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h5"
                                        component="p"
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        $
                                        {analyticsLoading
                                            ? '...'
                                            : analytics?.monthly_collections?.toLocaleString() ||
                                              '0'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Collected This Month
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <Box className={classes.summaryCard}>
                    <Card>
                        <CardContent>
                            <Box className={classes.cardContent}>
                                <Box
                                    className={`${classes.iconBox} ${classes.warningIcon}`}
                                >
                                    <AttachMoney />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h5"
                                        component="p"
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        $
                                        {analyticsLoading
                                            ? '...'
                                            : analytics?.total_overdue?.toLocaleString() ||
                                              '0'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Outstanding Balance
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <Box className={classes.summaryCard}>
                    <Card>
                        <CardContent>
                            <Box className={classes.cardContent}>
                                <Box
                                    className={`${classes.iconBox} ${classes.primaryIcon}`}
                                >
                                    <CreditCard />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h5"
                                        component="p"
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        $
                                        {analyticsLoading
                                            ? '...'
                                            : analytics?.processing_fees?.toLocaleString() ||
                                              '0'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Processing Fees
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                <Box className={classes.summaryCard}>
                    <Card>
                        <CardContent>
                            <Box className={classes.cardContent}>
                                <Box
                                    className={`${classes.iconBox} ${classes.errorIcon}`}
                                >
                                    <Person />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h5"
                                        component="p"
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        {analyticsLoading
                                            ? '...'
                                            : analytics?.tenants_overdue || '0'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Delinquent Accounts
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <Paper style={{ width: '100%' }}>
                <Tabs value={currentTab} onChange={handleTabChange}>
                    <Tab label="Tenant Bills" />
                    <Tab label="Delinquency" />
                    <Tab label="Settings" />
                </Tabs>

                {/* Tenant Bills Tab */}
                {currentTab === 0 && (
                    <Box
                        style={{
                            height: 'calc(100vh - 350px)',
                            minHeight: '400px',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <TenantBillTab />
                    </Box>
                )}

                {/* Delinquency Tab */}
                {currentTab === 1 && (
                    <Box
                        style={{
                            height: 'calc(100vh - 350px)',
                            minHeight: '400px',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <DelinquencyTab />
                    </Box>
                )}

                {/* Settings Tab */}
                {currentTab === 2 && (
                    <Box
                        style={{
                            height: 'calc(100vh - 350px)',
                            minHeight: '400px',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <TenantBillSettings />
                    </Box>
                )}
            </Paper>
        </Container>
    )
}
